"use client";

import { useMemo } from "react";
import SimilarCategoryProducts from "@/components/SimilarCategoryProducts";
import OtherRegionProducts from "@/components/OtherRegionProducts";
import PartnerProducts from "@/components/PartnerProducts";
import HotPlaceSection from "@/components/HotPlaceSection";
import { getCityIdByName } from "@/config/locations";
import type { Product } from "@/types";

interface RecommendationSectionsProps {
  products: Product[];
  locale: string;
  /** 카테고리 ID (비슷한 상품 추천용) */
  categoryId?: string;
  /** 현재 검색 지역 (다른 지역 추천용) */
  currentLocation?: string;
  /** 레이아웃 모드: horizontal(가로 스크롤) 또는 vertical(세로 그리드) */
  layout?: "horizontal" | "vertical";
  /** 하단 "인기 여행지" 섹션 표시 여부 — 검색 결과 페이지는 자체 추천이 있어 끈다 */
  showHotPlaces?: boolean;
}

export default function RecommendationSections({
  products,
  locale,
  categoryId,
  currentLocation,
  layout = "horizontal",
  showHotPlaces = true,
}: RecommendationSectionsProps) {
  // 현재 표시된 상품 ID 목록 (추천에서 제외용)
  const excludeProductIds = useMemo(
    () => products.map((p) => p.id),
    [products]
  );

  // 상품들에서 가장 많이 나오는 카테고리 추출 (categoryId가 없을 때 사용)
  const dominantCategoryId = useMemo(() => {
    if (categoryId) return categoryId;

    const categoryCount: Record<string, number> = {};
    products.forEach((p) => {
      if (p.category_code) {
        categoryCount[p.category_code] = (categoryCount[p.category_code] || 0) + 1;
      }
    });

    const sorted = Object.entries(categoryCount).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || undefined;
  }, [products, categoryId]);

  // 상품들에서 가장 많이 나오는 도시 ID 추출 (핫플레이스 우선순위용)
  const dominantCityId = useMemo(() => {
    const cityCount: Record<string, number> = {};

    products.forEach((p) => {
      // primary_city 또는 branch_locations에서 도시 추출
      const cities = p.primary_city
        ? [p.primary_city]
        : p.branch_locations || [];

      cities.forEach((cityName) => {
        const cityId = getCityIdByName(cityName);
        if (cityId) {
          cityCount[cityId] = (cityCount[cityId] || 0) + 1;
        }
      });
    });

    const sorted = Object.entries(cityCount).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || undefined;
  }, [products]);

  // 상품들에서 가장 많이 나오는 파트너 추출 (판매자의 다른 상품 추천용)
  const dominantPartner = useMemo(() => {
    const partnerCount: Record<string, { count: number; name?: string }> = {};

    products.forEach((p) => {
      if (p.partner_id) {
        if (!partnerCount[p.partner_id]) {
          partnerCount[p.partner_id] = { count: 0, name: p.partner_name };
        }
        partnerCount[p.partner_id].count += 1;
      }
    });

    const sorted = Object.entries(partnerCount).sort(
      (a, b) => b[1].count - a[1].count
    );

    if (sorted[0]) {
      return {
        partnerId: sorted[0][0],
        partnerName: sorted[0][1].name,
      };
    }
    return undefined;
  }, [products]);

  return (
    <>
      {/* 비슷한 카테고리 상품 */}
      {dominantCategoryId && (
        <SimilarCategoryProducts
          categoryId={dominantCategoryId}
          excludeProductIds={excludeProductIds}
          locale={locale}
          layout={layout}
        />
      )}

      {/* 판매자의 다른 상품 */}
      {dominantPartner && (
        <PartnerProducts
          partnerId={dominantPartner.partnerId}
          partnerName={dominantPartner.partnerName}
          excludeProductIds={excludeProductIds}
          locale={locale}
          layout={layout}
        />
      )}

      {/* 다른 지역의 상품 */}
      {currentLocation && (
        <OtherRegionProducts
          categoryId={dominantCategoryId}
          currentLocation={currentLocation}
          excludeProductIds={excludeProductIds}
          locale={locale}
          layout={layout}
        />
      )}

      {/* 핫플레이스 */}
      {showHotPlaces && (
        <HotPlaceSection
          locale={locale}
          priorityCityId={dominantCityId}
          layout={layout}
        />
      )}
    </>
  );
}
